import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useAddLabelTemplateMutation,
  useUpdateLabelTemplateMutation,
  useGetLabelTemplateByIdQuery,
} from "../toolkitServices/labelTemplateApi";
import PrnLabelTemplateEditor from "../components/labels/PrnLabelTemplateEditor";

const PrnLabelTemplateEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    data: tplRes,
    isLoading: isTemplateLoading,
    isError: isTemplateError,
  } = useGetLabelTemplateByIdQuery(id, {
    skip: !isEdit,
  });

  const [addLabelTemplate, { isLoading: isAdding }] =
    useAddLabelTemplateMutation();

  const [updateLabelTemplate, { isLoading: isUpdating }] =
    useUpdateLabelTemplateMutation();

  const isSaving = isAdding || isUpdating;

  const initialTemplate = useMemo(() => {
    if (!isEdit) {
      return {
        name: "",
        templateType: "prn",
        branchIds: [],
        categoryIds: [],
        appliesToAllBranches: false,
        appliesToAllCategories: false,
        prnCode: "",
      };
    }

    const data = tplRes?.data || tplRes;
    if (!data) return undefined;

    return {
      name: data?.name || "",
      templateType: "prn",
      branchIds: Array.isArray(data?.branchIds) ? data.branchIds : [],
      categoryIds: Array.isArray(data?.categoryIds) ? data.categoryIds : [],
      appliesToAllBranches: !!data?.appliesToAllBranches,
      appliesToAllCategories: !!data?.appliesToAllCategories,
      prnCode: data?.prnCode || "",
    };
  }, [isEdit, tplRes]);

  const handleSaveTemplate = async (template) => {
    try {
      const payload = {
        name: (template?.name || "").trim() || "Untitled PRN Label",
        templateType: "prn",
        branchIds: template?.appliesToAllBranches
          ? []
          : template?.branchIds || [],
        categoryIds: template?.appliesToAllCategories
          ? []
          : template?.categoryIds || [],
        appliesToAllBranches: !!template?.appliesToAllBranches,
        appliesToAllCategories: !!template?.appliesToAllCategories,
        prnCode: (template?.prnCode || "").trim(),
        elements: [],
      };

      if (!payload.name) {
        toast.error("Template name is required");
        return;
      }

      if (!payload.prnCode) {
        toast.error("PRN code is required");
        return;
      }

      if (isEdit) {
        await updateLabelTemplate({
          id,
          ...payload,
        }).unwrap();

        toast.success("PRN label template updated successfully!");
      } else {
        await addLabelTemplate(payload).unwrap();
        toast.success("PRN label template created successfully!");
      }

      navigate("/designs/prn-label-templates/list");
    } catch (err) {
      console.error("Error saving PRN label template:", err);

      const msg =
        err?.data?.message ||
        err?.data?.error ||
        err?.error ||
        "Failed to save PRN label template";

      toast.error(msg);
    }
  };

  if (isEdit && isTemplateLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-500">
        Loading PRN template...
      </div>
    );
  }

  if (isEdit && isTemplateError) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-sm text-red-500">
        Failed to load PRN template for editing.
      </div>
    );
  }

  if (isEdit && !initialTemplate) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-500">
        Preparing template...
      </div>
    );
  }

  return (
    <PrnLabelTemplateEditor
      initialTemplate={initialTemplate}
      onSave={handleSaveTemplate}
      isSaving={isSaving}
      isEdit={isEdit}
    />
  );
};

export default PrnLabelTemplateEditorPage;
